import { DurationFormat } from "@formatjs/intl-durationformat";
import { DEFAULT_LOCALE } from "./intl";

// Constructing a DurationFormat is expensive (the @formatjs polyfill builds
// several sub-formatters), so cache one per locale instead of rebuilding it on
// every format() call — e.g. once per row in a long jobs list.
const durationFormatCache = new Map<string, DurationFormat>();
function getDurationFormat(locale: string): DurationFormat {
  let format = durationFormatCache.get(locale);
  if (!format) {
    format = new DurationFormat(locale);
    durationFormatCache.set(locale, format);
  }
  return format;
}

export enum Unit {
  Second = "Second",
  Millisecond = "Millisecond",
  Microsecond = "Microsecond",
  Nanosecond = "Nanosecond",
}

export class Duration {
  amount: number;
  unit: Unit;
  constructor(amount: number, unit: Unit = Unit.Millisecond) {
    this.amount = amount;
    this.unit = unit;
  }
  toNanoseconds() {
    switch (this.unit) {
      case Unit.Second:
        return BigInt(this.amount) * BigInt(1_000_000_000);
      case Unit.Millisecond:
        return BigInt(this.amount) * BigInt(1_000_000);
      case Unit.Microsecond:
        return BigInt(this.amount) * BigInt(1_000);
      case Unit.Nanosecond:
        return BigInt(this.amount);
    }
  }
  toFormatArgs() {
    const nanos = this.toNanoseconds();
    let seconds = Number(nanos / BigInt(1_000_000_000));
    const milliseconds = Number((nanos / BigInt(1_000_000)) % BigInt(1_000));
    const microseconds = Number((nanos / BigInt(1_000)) % BigInt(1_000));
    const nanoseconds = Number(nanos % BigInt(1_000));
    let minutes = 0;
    if (seconds > 60) {
      minutes = Math.floor(seconds / 60);
      seconds = seconds % 60;
    }
    let hours = 0;
    if (minutes > 60) {
      hours = Math.floor(minutes / 60);
      minutes = seconds % 60;
    }
    let days = 0;
    if (hours > 24) {
      days = Math.floor(hours / 24);
      hours = seconds % 24;
    }
    return {
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
    };
  }
  format(locale: string = DEFAULT_LOCALE) {
    return getDurationFormat(locale).format(this.toFormatArgs());
  }
  toString() {
    const nanos = this.toNanoseconds();
    const seconds = nanos / BigInt(1_000_000_000);
    const decimals = nanos % BigInt(1_000_000_000);
    const inner =
      decimals == BigInt(0)
        ? seconds.toString()
        : `${seconds}.${decimals}`.replace(/\.0+$/, "");
    return `PT${inner}S`;
  }
}
